import { useEffect } from "react";
import useOnEscape from "../../hooks/useOnEscape";

/**
 * CenteredModal — desktop-only centered dialog for add/edit forms.
 *
 * Button rules (enforce in children):
 *   Primary CTA  → className="btn btn-primary"            (rose fill)
 *   Secondary    → className="btn btn-secondary"          (outline)
 *   Cancel       → className="btn btn-ghost hm-modal-cancel"  (grey text link — never teal/rose)
 *   Destructive  → className="btn hm-modal-btn--destructive"  (red text — ··· dropdown only, always last)
 */
export default function CenteredModal({ title, onClose, children, width = 480 }) {
  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape
  useOnEscape(onClose);

  return (
    <div
      className="centered-modal-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 220,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: "rgba(28, 25, 23, 0.45)",
      }}
      onClick={onClose}
    >
      <style>{`
        @media (min-width: 768px) {
          .centered-modal-card,
          .centered-modal-card * {
            box-sizing: border-box;
          }

          .centered-modal-card .hm-util-drawer-head,
          .centered-modal-card .hm-veh-add-panel > .hm-panel-head,
          .centered-modal-card .hm-sub-quickadd > .hm-panel-head {
            display: none;
          }

          .centered-modal-card form,
          .centered-modal-card input,
          .centered-modal-card select,
          .centered-modal-card textarea,
          .centered-modal-card button {
            max-width: 100%;
          }

          .centered-modal-card .hm-vault-add-desktop-form,
          .centered-modal-card .hm-vault-add-desktop-types,
          .centered-modal-card .hm-vault-add-desktop-fields,
          .centered-modal-card .hm-veh-form,
          .centered-modal-card .hm-sub-drawer-body,
          .centered-modal-card .hm-util-drawer-form,
          .centered-modal-card .hm-util-ledger-form {
            display: grid;
            grid-template-columns: minmax(0, 1fr);
            gap: 14px;
            width: 100%;
          }

          .centered-modal-card .hm-vault-add-type-picker--desktop,
          .centered-modal-card .hm-vault-add-type-picker,
          .centered-modal-card .hm-util-type-picker,
          .centered-modal-card .hm-sub-category-picker,
          .centered-modal-card .hm-sub-qa-grid {
            display: grid;
            grid-template-columns: minmax(0, 1fr);
            gap: 8px;
          }

          .centered-modal-card .hm-vault-add-desktop-fields label,
          .centered-modal-card .hm-util-drawer-form label,
          .centered-modal-card .hm-util-ledger-form label,
          .centered-modal-card .hm-veh-form label,
          .centered-modal-card .hm-util-ledger-span,
          .centered-modal-card .hm-veh-form-span2 {
            grid-column: 1 / -1;
            min-width: 0;
          }

          .centered-modal-card .hm-veh-add-panel,
          .centered-modal-card .hm-sub-quickadd {
            margin: 0;
            padding: 0;
            border: 0;
            box-shadow: none;
            background: transparent;
          }

          .centered-modal-card .hm-vault-form-actions,
          .centered-modal-card .hm-util-drawer-footer,
          .centered-modal-card .hm-util-ledger-actions {
            display: flex;
            gap: 10px;
            justify-content: flex-end;
            flex-wrap: wrap;
          }

          .centered-modal-card .hm-gifts-action-sheet {
            position: static;
            width: 100%;
            max-width: none;
            transform: none;
            box-shadow: none;
            border: 0;
            padding: 0;
            background: transparent;
          }

          .centered-modal-card .hm-vin-row {
            display: grid;
            grid-template-columns: minmax(0, 1fr);
            gap: 8px;
          }

          .centered-modal-card .hm-vin-btn,
          .centered-modal-card .hm-vin-scan-btn {
            width: 100%;
            min-height: 42px;
            justify-content: center;
          }
        }
      `}</style>
      <div
        className="centered-modal-card"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          width,
          maxWidth: "calc(100vw - 40px)",
          maxHeight: "calc(100dvh - 40px)",
          overflowY: "auto",
          background: "var(--warm-white)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          padding: 24,
          boxShadow: "var(--shadow-lg)",
          overscrollBehavior: "contain",
          touchAction: "pan-y",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="centered-modal-header"
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 16 }}
        >
          <h3 style={{ fontSize: 20 }}>{title}</h3>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            x
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
